import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import MenuItem from '@mui/material/MenuItem'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, RemoteContent, Title, formatDate, formatRange, useAuth, useSnackbar } from '@jupi/ui'
import { formatMinutes, type LessonSlot, type SuggestLessonSlotsResult } from '@jupi/shared'
import { useEnrolments, useProgress } from '../api/school'
import { useMyAircraft, useSuggestSlots } from '../api/lessons'
import { useCreateBooking } from '../api/queries'
import { clubParts } from '../features/lessons/clubTime'
import { AircraftChips, MyAircraftDialog } from '../features/lessons/MyAircraft'

const DURATIONS = [60, 90, 120, 150, 180]

// The quick way to book a lesson: pick the course and lesson, see the next
// times the instructor is free and an aircraft is too, and book one. A
// student gets their own instructor's times; an instructor books a student
// in the same way. Everything is still checked by the booking API.
export default function BookingWizardPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { showSnackbar } = useSnackbar()
  const { data: enrolments = [], isLoading, error } = useEnrolments()
  const suggest = useSuggestSlots()
  const create = useCreateBooking()
  const mine = useMyAircraft()
  const [aircraftOpen, setAircraftOpen] = useState(false)

  const [step, setStep] = useState(0)
  const [enrolmentId, setEnrolmentId] = useState<number | null>(null)
  const [lessonId, setLessonId] = useState<number | null>(null)
  const [duration, setDuration] = useState(90)
  const [instructorUserId, setInstructorUserId] = useState<string | null>(null)
  const [from, setFrom] = useState<string | undefined>(undefined)
  const [result, setResult] = useState<SuggestLessonSlotsResult | null>(null)
  const [slot, setSlot] = useState<LessonSlot | null>(null)
  const [registration, setRegistration] = useState('')
  const [flightHours, setFlightHours] = useState('')
  const [comment, setComment] = useState('')
  // The lesson as booked, shown once it is made.
  const [booked, setBooked] = useState<{ slot: LessonSlot; registration: string; lesson: string | null } | null>(null)
  const [violations, setViolations] = useState<{ code: string; [key: string]: unknown }[]>([])

  // Active courses: the member's own, and the students' they instruct.
  const active = useMemo(() => enrolments.filter((e) => e.status === 'active'), [enrolments])
  useEffect(() => {
    if (enrolmentId === null && active.length === 1) setEnrolmentId(active[0]!.enrolmentId)
  }, [active, enrolmentId])
  const enrolment = active.find((e) => e.enrolmentId === enrolmentId)

  const { data: progress } = useProgress(enrolment ? enrolment.enrolmentId : Number.NaN)
  const lessons = useMemo(() => progress?.modules.flatMap((m) => m.lessons) ?? [], [progress])
  useEffect(() => {
    if (enrolment && lessonId === null && lessons.length > 0) {
      setLessonId(lessons.find((l) => l.status !== 'completed')?.lessonId ?? null)
    }
  }, [enrolment, lessons, lessonId])

  const search = async (options: { from?: string; instructor?: string | null } = {}) => {
    if (!enrolment) return
    const instructor = options.instructor === undefined ? instructorUserId : options.instructor
    setSlot(null)
    setFrom(options.from)
    const body = await suggest.mutateAsync({
      enrolmentId: enrolment.enrolmentId,
      durationMinutes: duration,
      ...(instructor ? { instructorUserId: instructor } : {}),
      ...(options.from ? { from: options.from } : {}),
    })
    setResult(body)
    setInstructorUserId(body.instructor?.userId ?? instructor ?? null)
    setStep(1)
  }

  const choose = (picked: LessonSlot) => {
    setSlot(picked)
    setRegistration(picked.registration)
    setFlightHours(formatMinutes(Math.round((result?.estimatedFlightHours ?? 1) * 60)))
    setViolations([])
    setStep(2)
  }

  const book = async () => {
    if (!enrolment || !slot || !result?.instructor) return
    const start = clubParts(slot.startsAt)
    const end = clubParts(slot.endsAt)
    setViolations([])
    try {
      await create.mutateAsync({
        registration,
        startDate: start.date,
        startTime: start.time,
        endDate: end.date,
        endTime: end.time,
        estimatedFlightHours: flightHours,
        comment: comment || null,
        bookingType: 'training',
        ...(enrolment.studentUserId !== user?.id ? { userId: enrolment.studentUserId } : {}),
        enrolmentId: enrolment.enrolmentId,
        lessonId,
        instructorUserId: result.instructor.userId,
      })
      const picked = lessons.find((l) => l.lessonId === lessonId)
      setBooked({ slot, registration, lesson: picked ? `${picked.lessonNumber} ${picked.title}` : null })
      setStep(3)
    } catch (err) {
      if (err instanceof ApiError && err.violations.length > 0) {
        setViolations(err.violations)
        // Someone took the time while it was on screen: back to a fresh list.
        if (err.status === 409) setStep(1)
      } else {
        showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
      }
    }
  }

  const days = useMemo(() => {
    const byDay = new Map<string, LessonSlot[]>()
    for (const s of result?.slots ?? [])
      byDay.set(clubParts(s.startsAt).date, [...(byDay.get(clubParts(s.startsAt).date) ?? []), s])
    return [...byDay.entries()]
  }, [result])

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Title label={t('lessons.wizardTitle')} />
      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {['stepWhat', 'stepWhen', 'stepConfirm'].map((key) => (
          <Step key={key}>
            <StepLabel>{t(`lessons.${key}`)}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <RemoteContent isLoading={isLoading} error={error}>
        {active.length === 0 ? <Alert severity="info">{t('booking.type.noEnrolment')}</Alert> : null}

        {step === 0 && active.length > 0 ? (
          <Stack spacing={2}>
            <TextField
              select
              label={t('school.course')}
              value={enrolmentId ?? ''}
              onChange={(e) => {
                setEnrolmentId(Number(e.target.value))
                setLessonId(null)
                setInstructorUserId(null)
              }}
            >
              {active.map((e) => (
                <MenuItem key={e.enrolmentId} value={e.enrolmentId}>
                  {e.studentUserId === user?.id ? e.courseName : `${e.studentName} · ${e.courseName}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('school.lesson')}
              value={lessonId ?? ''}
              onChange={(e) => setLessonId(e.target.value === '' ? null : Number(e.target.value))}
              disabled={!enrolment}
            >
              <MenuItem value="">{t('school.extraFlight')}</MenuItem>
              {lessons.map((l) => (
                <MenuItem key={l.lessonId} value={l.lessonId}>
                  {l.lessonNumber} {l.title}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('lessons.duration')}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              helperText={t('lessons.durationHelp')}
            >
              {DURATIONS.map((m) => (
                <MenuItem key={m} value={m}>
                  {t('lessons.minutes', { count: m })}
                </MenuItem>
              ))}
            </TextField>
            {enrolment && enrolment.studentUserId === user?.id && mine.data ? (
              <Box>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('lessons.myAircraft')}
                  </Typography>
                  <Button size="small" onClick={() => setAircraftOpen(true)}>
                    {t('common.edit')}
                  </Button>
                </Stack>
                {mine.data.registrations.length > 0 ? (
                  <AircraftChips value={mine.data} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('lessons.allAircraft')}
                  </Typography>
                )}
                <MyAircraftDialog open={aircraftOpen} value={mine.data} onClose={() => setAircraftOpen(false)} />
              </Box>
            ) : null}
            <Box>
              <Button
                variant="contained"
                disabled={!enrolment || suggest.isPending}
                onClick={() => search()}
                startIcon={<Icon icon="mdi:calendar-search" />}
              >
                {t('lessons.findTimes')}
              </Button>
            </Box>
          </Stack>
        ) : null}

        {step === 1 && result ? (
          <Stack spacing={2}>
            {result.instructor ? (
              <Typography>
                <Icon icon="mdi:account-tie" style={{ verticalAlign: -3, marginRight: 6 }} />
                {t('lessons.withInstructor', { name: result.instructor.displayName })}
              </Typography>
            ) : (
              <>
                <Alert severity="info">{t('lessons.chooseInstructor')}</Alert>
                <TextField
                  select
                  label={t('school.instructor')}
                  value={instructorUserId ?? ''}
                  onChange={(e) => search({ instructor: e.target.value })}
                >
                  {result.candidates.map((c) => (
                    <MenuItem key={c.userId} value={c.userId}>
                      {c.displayName}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
            {result.instructor && !result.hasAvailability ? (
              <Alert severity="warning">{t('lessons.instructorNoAvailability')}</Alert>
            ) : null}
            {result.instructor && result.hasAvailability && result.slots.length === 0 ? (
              <Alert severity="info">{t('lessons.noSlots')}</Alert>
            ) : null}
            {days.map(([day, slots]) => (
              <Box key={day}>
                <Typography variant="overline" color="text.secondary">
                  {formatDate(slots[0]!.startsAt)}
                </Typography>
                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' } }}>
                  {slots.map((s) => (
                    <Card key={s.startsAt} variant="outlined">
                      <CardActionArea onClick={() => choose(s)}>
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography sx={{ fontWeight: 600 }}>{formatRange(s.startsAt, s.endsAt)}</Typography>
                          {result.fleet.length > 0 ? (
                            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {result.fleet.map((r) => {
                                const free = s.registrations.includes(r)
                                const unrated = result.unrated.includes(r)
                                const favourite = r === result.preferred
                                return (
                                  <Chip
                                    key={r}
                                    size="small"
                                    label={r}
                                    color={favourite && free ? 'primary' : 'default'}
                                    variant={free ? (favourite ? 'filled' : 'outlined') : 'outlined'}
                                    icon={favourite ? <Icon icon="mdi:star" /> : undefined}
                                    // Taken at that time: shown, but faded.
                                    sx={
                                      free
                                        ? undefined
                                        : unrated
                                          ? { opacity: 0.35 }
                                          : { opacity: 0.35, textDecoration: 'line-through' }
                                    }
                                    title={unrated ? String(t('lessons.notRated')) : undefined}
                                  />
                                )
                              })}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {s.registrations.join(', ')}
                            </Typography>
                          )}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>
              </Box>
            ))}
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setStep(0)}>{t('common.back')}</Button>
              {result.slots.length > 0 ? (
                <Button
                  onClick={() =>
                    search({ from: new Date(Date.parse(result.slots.at(-1)!.startsAt) + 30 * 60_000).toISOString() })
                  }
                  disabled={suggest.isPending}
                >
                  {t('lessons.laterTimes')}
                </Button>
              ) : null}
              {from ? (
                <Button onClick={() => search()} disabled={suggest.isPending}>
                  {t('lessons.fromStart')}
                </Button>
              ) : null}
            </Stack>
          </Stack>
        ) : null}

        {step === 3 && booked && result?.instructor ? (
          <Stack spacing={2}>
            <Alert severity="success">{t('booking.created')}</Alert>
            <Card variant="outlined">
              <CardContent>
                <Typography sx={{ fontWeight: 600 }}>
                  {formatDate(booked.slot.startsAt)} · {formatRange(booked.slot.startsAt, booked.slot.endsAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {enrolment?.studentName} · {enrolment?.courseName}
                  {booked.lesson ? ` · ${booked.lesson}` : ''}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip size="small" icon={<Icon icon="mdi:airplane" />} label={booked.registration} />
                  <Chip size="small" icon={<Icon icon="mdi:account-tie" />} label={result.instructor.displayName} />
                </Stack>
              </CardContent>
            </Card>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Button variant="contained" component={RouterLink} to="/bookings/calendar">
                {t('lessons.toCalendar')}
              </Button>
              <Button
                onClick={() => {
                  setBooked(null)
                  setSlot(null)
                  setResult(null)
                  setComment('')
                  setStep(0)
                }}
              >
                {t('lessons.bookAnother')}
              </Button>
            </Stack>
          </Stack>
        ) : null}

        {step === 2 && slot && result?.instructor ? (
          <Stack spacing={2}>
            {violations.length > 0 ? (
              <Alert severity="warning">
                <AlertTitle>{t('rules.refused')}</AlertTitle>
                <Stack component="ul" sx={{ pl: 2, m: 0 }}>
                  {violations.map((v) => (
                    <li key={v.code}>{String(t(`rules.${v.code}`, v as never))}</li>
                  ))}
                </Stack>
              </Alert>
            ) : null}
            <Card variant="outlined">
              <CardContent>
                <Typography sx={{ fontWeight: 600 }}>
                  {formatDate(slot.startsAt)} · {formatRange(slot.startsAt, slot.endsAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {enrolment?.studentName} · {enrolment?.courseName}
                </Typography>
                <Chip
                  size="small"
                  sx={{ mt: 1 }}
                  icon={<Icon icon="mdi:account-tie" />}
                  label={result.instructor.displayName}
                />
              </CardContent>
            </Card>
            <TextField
              select
              label={t('booking.aircraft')}
              value={registration}
              onChange={(e) => setRegistration(e.target.value)}
            >
              {slot.registrations.map((r) => (
                <MenuItem key={r} value={r}>
                  {r === result.preferred ? `★ ${r}` : r}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('booking.flightTime')}
              value={flightHours}
              onChange={(e) => setFlightHours(e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'text' } }}
            />
            <TextField
              label={t('booking.comment')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              multiline
            />
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setStep(1)}>{t('common.back')}</Button>
              <Button variant="contained" onClick={book} disabled={create.isPending}>
                {t('lessons.book')}
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </RemoteContent>
    </Box>
  )
}
